import React, { useContext, useState, useEffect } from 'react';
import { AuthContext, API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check, Sparkles, LogOut } from 'lucide-react';
import { showToast } from '@/lib/custom-toast';
import axios from 'axios';

const PaymentLockOverlay = () => {
  const { token, user, logout, refreshUser } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly');

  // Lógica de bloqueo real: 
  const hasCard = user?.has_payment_method === true;
  const isPremiumOrPro = (user?.subscription_tier === 'premium' || user?.subscription_tier === 'pro') && 
                         (user?.subscription_status === 'active' || user?.subscription_status === 'trialing');
  const isManual = user?.is_manual_grant === true;

  // Bloquear el scroll del body cuando el overlay está activo
  useEffect(() => {
    const shouldBlock = token && user && !hasCard && !isPremiumOrPro && !isManual && user.role !== 'admin';
    if (shouldBlock) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [token, user, hasCard, isPremiumOrPro, isManual]);

  // Si no hay usuario, NO mostrar nada (esperar a que cargue App.js)
  if (!token || !user) return null;

  // Si es admin, saltar bloqueo
  if (user.role === 'admin') return null;

  // Si ya es Premium/Pro (por trial o manual) o tiene tarjeta, saltar bloqueo
  if (hasCard || isPremiumOrPro || isManual) {
    return null;
  }

  const priceIds = {
    premiumMonthly: 'price_1TUYglJ42rbYbursKjX3zUVE',
    premiumAnnual: 'price_1TUYgmJ42rbYbursfoGFcXfR',
    proMonthly: 'price_1TUYgmJ42rbYburs4pPjKFaf',
    proAnnual: 'price_1TUYgmJ42rbYbursvzzPg8xX'
  };

  const handleSubscribe = async (priceId, planName) => {
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/subscriptions/create-checkout-session`,
        { price_id: priceId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      showToast.error(error.response?.data?.detail || 'Error al conectar con Stripe');
    } finally {
      setLoading(false);
    }
  };

  const handleActivateFree = async () => {
    setLoading(true);
    try {
      await axios.post(
        `${API}/subscriptions/activate-free-plan`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast.success('¡Plan Gratis activado! Bienvenido.');
      await refreshUser();
    } catch (error) {
      showToast.error('No se pudo activar el plan gratis');
    } finally {
      setLoading(false);
    }
  };


  const features = {
    free: [
      'Transacciones manuales ilimitadas',
      'Hasta 3 cuentas bancarias',
      'Categorías personalizadas',
      'Dashboard con estadísticas básicas',
      'Transacciones recurrentes',
      'Registro de inversiones'
    ],
    premium: [
      '✨ Todo de GRATIS +',
      '💳 Hasta 10 cuentas bancarias',
      '🎙️ 100 transacciones por voz/mes',
      '🤖 IA financiera + Chat inteligente',
      '🎯 Objetivos financieros',
      '📊 Análisis predictivos',
      '🔔 Notificaciones automáticas'
    ],
    pro: [
      '💎 Todo de PREMIUM +',
      '💳 Cuentas bancarias ilimitadas',
      '🎙️ 300 transacciones por voz/mes',
      '📄 Reportes PDF exportables',
      '🚀 Soporte prioritario',
      '📈 Análisis avanzados',
      '📊 Exportar a Excel/CSV'
    ]
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-start justify-center p-4 sm:p-6 overflow-y-auto bg-slate-900/60 backdrop-blur-md animate-in fade-in duration-500 py-12">
      <div className="max-w-6xl w-full relative">
        
        {/* Mensaje de Confianza (Banner que pediste) */}
        <div className="mb-8 p-6 bg-blue-600 rounded-[2rem] text-white shadow-2xl shadow-blue-500/20 flex flex-col sm:flex-row items-center gap-4 border border-blue-400/30">
          <div className="p-4 bg-white/20 rounded-2xl shrink-0">
            <Sparkles className="w-10 h-10 text-white" />
          </div>
          <div className="text-center sm:text-left flex-1">
            <h2 className="text-2xl font-bold mb-1">¡Elige tu plan para comenzar! 🚀</h2>
            <p className="text-blue-50 text-sm sm:text-base font-medium opacity-90">
              Disfruta de todas las herramientas financieras que tenemos para ti. 
            </p>
            <p className="mt-1 font-bold text-white text-sm sm:text-base">
              Puedes empezar con el Plan Gratis sin necesidad de registrar ninguna tarjeta.
            </p>
          </div>
        </div>

        <div className="bg-white rounded-[2.5rem] p-8 sm:p-12 shadow-2xl relative overflow-hidden">
          {/* Header del Bloqueo */}
          <div className="text-center mb-10">
            <h3 className="text-3xl font-black text-slate-900 mb-4">Activa tu Acceso Total</h3>
            <p className="text-slate-500 font-medium max-w-xl mx-auto">
              Únete a nuestra comunidad y domina tus finanzas con el poder de la IA. Elige el plan que mejor se adapte a ti.
            </p>

            {/* Billing Toggle */}
            <div className="mt-8 inline-flex items-center p-1.5 bg-slate-100 rounded-2xl shadow-inner border border-slate-200">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-8 py-2.5 rounded-xl font-bold text-sm transition-all ${billingCycle === 'monthly' ? 'bg-white text-slate-900 shadow-md transform scale-105' : 'text-slate-500 hover:text-slate-700'}`}
              >
                Mensual
              </button>
              <button
                onClick={() => setBillingCycle('annual')}
                className={`px-8 py-2.5 rounded-xl font-bold text-sm transition-all flex items-center gap-2 ${billingCycle === 'annual' ? 'bg-white text-blue-600 shadow-md transform scale-105' : 'text-slate-500 hover:text-slate-700'}`}
              >
                Anual
                <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-[10px] font-black">AHORRA MÁXIMO</span>
              </button>
            </div>
          </div>

          {/* Grid de Planes */}
          <div className={`grid grid-cols-1 gap-6 ${user?.subscription_tier === 'pro' ? 'md:grid-cols-2' : 'md:grid-cols-3'}`}>
            
            {/* Plan Gratis */}
            <Card className="p-6 border-2 border-slate-100 flex flex-col hover:border-slate-200 transition-all">
              <div className="text-center mb-6">
                <h4 className="text-xl font-bold text-slate-800">Plan Gratis</h4>
                <div className="text-3xl font-black text-slate-900 my-2">$0 <span className="text-sm font-bold">MXN</span></div>
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Sin tarjeta requerida</p>
              </div>
              <ul className="space-y-3 mb-8 flex-1">
                {features.free.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-slate-600">
                    <Check className="w-4 h-4 text-green-500 shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Button 
                variant="outline" 
                className="w-full py-6 rounded-xl font-bold border-2"
                onClick={handleActivateFree}
                disabled={loading}
              >
                Activar Gratis
              </Button>
            </Card>

            {/* Plan Premium (oculto si ya es Pro) */}
            {user?.subscription_tier !== 'pro' && (
            <Card className="p-6 border-4 border-blue-500 relative flex flex-col shadow-xl scale-105 bg-white">
              <div className="absolute top-0 right-0 bg-blue-600 text-white px-3 py-1 text-[10px] font-black uppercase rounded-bl-lg">RECOMENDADO</div>
              <div className="text-center mb-6">
                <h4 className="text-xl font-bold text-slate-800">Premium</h4>
                <div className="my-2">
                  <div className="text-sm font-bold text-slate-400 line-through">
                    {billingCycle === 'monthly' ? '$99' : '$79'} MXN
                  </div>
                  <div className="text-4xl font-black text-blue-600">
                    {billingCycle === 'monthly' ? '$79' : '$50'} <span className="text-lg font-bold">MXN</span>
                  </div>
                </div>
                <p className="text-[10px] text-blue-600 font-black uppercase tracking-tighter">OFERTA LANZAMIENTO</p>
              </div>
              <ul className="space-y-3 mb-8 flex-1">
                {features.premium.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-slate-700 font-medium">
                    <Check className="w-4 h-4 text-blue-500 shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Button 
                className="w-full bg-blue-600 hover:bg-blue-700 py-6 rounded-xl font-black text-base shadow-lg"
                onClick={() => handleSubscribe(billingCycle === 'monthly' ? priceIds.premiumMonthly : priceIds.premiumAnnual, 'premium')}
                disabled={loading}
              >
                Suscribirme
              </Button>
            </Card>
            )}

            {/* Plan Pro */}
            <Card className="p-6 border-2 border-purple-100 flex flex-col hover:border-purple-200 transition-all">
              <div className="text-center mb-6">
                <h4 className="text-xl font-bold text-slate-800">Plan Pro</h4>
                <div className="my-2">
                  <div className="text-sm font-bold text-slate-400 line-through">
                    {billingCycle === 'monthly' ? '$149' : '$129'} MXN
                  </div>
                  <div className="text-3xl font-black text-purple-600">
                    {billingCycle === 'monthly' ? '$129' : '$83'} <span className="text-base font-bold">MXN</span>
                  </div>
                </div>
                <p className="text-[10px] text-purple-600 font-black uppercase tracking-tighter">PRECIO LANZAMIENTO</p>
              </div>
              <ul className="space-y-3 mb-8 flex-1">
                {features.pro.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-slate-600 font-medium">
                    <Check className="w-4 h-4 text-purple-500 shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Button 
                className="w-full bg-slate-900 hover:bg-black py-6 rounded-xl font-bold"
                onClick={() => handleSubscribe(billingCycle === 'monthly' ? priceIds.proMonthly : priceIds.proAnnual, 'pro')}
                disabled={loading}
              >
                Obtener Pro
              </Button>
            </Card>
          </div>

          <div className="mt-8 text-center">
            <p className="text-[11px] text-slate-400 font-bold uppercase tracking-[0.2em]">
              Seguridad de nivel bancario protegida por <span className="text-blue-500">Stripe</span>
            </p>
          </div>
        </div>

        {/* Logout Button (Movido al fondo) */}
        <div className="flex justify-center mt-8 pb-8">
          <Button 
            variant="ghost" 
            className="text-white/60 hover:text-white hover:bg-white/10 gap-2 font-bold px-6 rounded-full transition-all"
            onClick={logout}
          >
            <LogOut className="w-4 h-4" />
            Cerrar Sesión (Cambiar de cuenta)
          </Button>
        </div>

      </div>
    </div>
  );
};

export default PaymentLockOverlay;
